import { ApplicationRef, createComponent, Injectable, Injector, Type } from '@angular/core';
import { ModalRef } from './modal-ref';

export interface ModalConfig {
  data?: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class ModalService {

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  open<T>(component: Type<T>, config: ModalConfig = {}): ModalRef<T> {
    const modalRef = new ModalRef<T>();

    // DOM: backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';

    // DOM: modal wrapper
    const modalEl = document.createElement('div');
    modalEl.className = 'modal d-block';
    modalEl.style.cssText = 'background:rgba(0,0,0,.5)';
    modalEl.setAttribute('tabindex', '-1');
    modalEl.setAttribute('role', 'dialog');

    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog modal-dialog-centered';

    const content = document.createElement('div');
    content.className = 'modal-content';

    dialog.appendChild(content);
    modalEl.appendChild(dialog);

    // Close logic
    const closeFn = (value?: any) => {
      document.removeEventListener('keydown', escHandler);
      modalRef.onClose.next(value);
      modalRef.onClose.complete();
      this.appRef.detachView(compRef.hostView);
      compRef.destroy();
      if (document.body.contains(backdrop)) document.body.removeChild(backdrop);
      if (document.body.contains(modalEl)) document.body.removeChild(modalEl);
      document.body.classList.remove('modal-open');
    };

    modalRef._setCloseFn(closeFn);

    // Close on backdrop click
    modalEl.addEventListener('click', (e) => {
      if (e.target === modalEl) closeFn();
    });

    // Close on ESC
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFn();
    };
    document.addEventListener('keydown', escHandler);

    // Create component with ModalRef injected
    const elementInjector = Injector.create({
      providers: [{ provide: ModalRef, useValue: modalRef }],
      parent: this.injector,
    });

    const compRef = createComponent(component as any, {
      environmentInjector: this.appRef.injector,
      elementInjector,
    });

    // Apply data properties directly on instance (same as MDB behavior)
    if (config.data) {
      Object.assign(compRef.instance as any, config.data);
    }

    content.appendChild(compRef.location.nativeElement);
    document.body.appendChild(backdrop);
    document.body.appendChild(modalEl);
    document.body.classList.add('modal-open');

    this.appRef.attachView(compRef.hostView);
    compRef.changeDetectorRef.detectChanges();

    return modalRef;
  }
}
